/**
 * I18nContext — single i18n context with three namespaces: `site` (tenant
 * storefront), `marketing` (platform marketing site) and `admin` (tenant
 * dashboard). One provider at the app root; `I18nScope` overrides a namespace
 * for a subtree (URL-driven /es routes).
 *
 *   const t = useT("admin");            // t("nav.dashboard")
 *   const { lang, setLang } = useLang("admin");
 *   <I18nScope ns="marketing" lang="es">…</I18nScope>   // URL-driven override
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import admin from "../i18n/admin";
import marketing from "../i18n/marketing";
import site from "../i18n/site";

const STRINGS = { site, marketing, admin };
const DEFAULT_LANGS = { site: "en", marketing: "en", admin: "en" };

const I18nContext = createContext({ langs: DEFAULT_LANGS, setLang: () => {} });

// Root provider. Only the admin language is user-toggled (persisted in localStorage).
export function I18nProvider({ children }) {
  const [adminLang, setAdminLang] = useState(() => {
    try { return localStorage.getItem("admin_lang") || "en"; } catch { return "en"; }
  });

  const value = useMemo(() => ({
    langs: { ...DEFAULT_LANGS, admin: adminLang },
    setLang: (ns, next) => {
      if (ns !== "admin") return;
      setAdminLang(next);
      try { localStorage.setItem("admin_lang", next); } catch {}
    },
  }), [adminLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// Subtree override for a namespace (e.g. /es marketing routes, /es storefront pages).
export function I18nScope({ ns, lang, children }) {
  const parent = useContext(I18nContext);
  const value = useMemo(() => ({ ...parent, langs: { ...parent.langs, [ns]: lang } }), [parent, ns, lang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useLang(ns) {
  const { langs, setLang } = useContext(I18nContext);
  const lang = langs[ns] || "en";
  useEffect(() => {
    if (ns === "admin") { try { document.documentElement.lang = lang; } catch {} }
  }, [ns, lang]);
  return { lang, setLang: (next) => setLang(ns, next) };
}

export function useT(ns) {
  const { lang } = useLang(ns);
  const dict = STRINGS[ns];
  return (key, vars = {}) => {
    const raw = dict[lang]?.[key] ?? dict.en[key] ?? key;
    return Object.keys(vars).reduce((s, k) => s.replace(new RegExp(`{${k}}`, "g"), vars[k]), raw);
  };
}

/** Marketing URL helper — prefix a path with /es when the marketing lang is "es". */
export function useMarketingPath() {
  const { lang } = useLang("marketing");
  return (path) => {
    if (lang !== "es") return path;
    if (path === "/es" || path.startsWith("/es/")) return path;
    if (path === "/") return "/es";
    return `/es${path}`;
  };
}

/** Strip any /es prefix. Useful for language toggles. */
export function stripEsPrefix(path) {
  if (!path) return "/";
  if (path === "/es") return "/";
  if (path.startsWith("/es/")) return path.slice(3);
  return path;
}

export default I18nContext;
